import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Sparkles } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import HeroSlider from "../../components/event_media/HeroSlider";
import ConferenceFilters from "../../components/event_media/ConferenceFilters";
import ConferenceCard from "../../components/event_media/ConferenceCard";
import Images from "../../utils/Images";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import AuthenticatedFooter from "../../components/landing/AuthenticatedFooter";

const ROUTE_CONFIG = {
  "/event-media-sales/conference": {
    defaultFilter: "conferences",
    title: "Conferences",
    subtitle: "Connect, learn, and engage with industry leaders",
    placeholder: "Search conferences...",
  },
  "/event-media-sales/trade-mission": {
    defaultFilter: "trade_missions",
    title: "Trade Missions",
    subtitle: "Explore markets and build lasting partnerships",
    placeholder: "Search trade missions...",
  },
  "/event-media-sales/conference-trade-mission": {
    defaultFilter: "all",
    title: "Conference & Trade Mission",
    subtitle: "Discover conferences and trade missions you can promote",
    placeholder: "Search conferences and trade missions...",
  },
};

function getConfigFromPath(pathname) {
  const exact = ROUTE_CONFIG[pathname];
  if (exact) return exact;
  if (pathname.startsWith("/event-media-sales/conference-trade-mission"))
    return ROUTE_CONFIG["/event-media-sales/conference-trade-mission"];
  if (pathname.startsWith("/event-media-sales/conference"))
    return ROUTE_CONFIG["/event-media-sales/conference"];
  if (pathname.startsWith("/event-media-sales/trade-mission"))
    return ROUTE_CONFIG["/event-media-sales/trade-mission"];
  return ROUTE_CONFIG["/event-media-sales/conference-trade-mission"];
}

export default function ConferenceTradeMission() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const config = useMemo(() => getConfigFromPath(pathname), [pathname]);

  const [selectedFilter, setSelectedFilter] = useState(config.defaultFilter);
  const [searchQuery, setSearchQuery] = useState("");
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSelectedFilter(config.defaultFilter);
  }, [config.defaultFilter]);

  const heroImages = [Images.hero_img_web_three, Images.hero_img_web_four].filter(Boolean);
  const heroSlides = heroImages.length ? heroImages : [Images.hero_img_web_one];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await conferenceAndTradeMissionService.getConferenceAndTradeMission();
        const list = response.list ?? (response.data?.data ?? response.data);
        const arr = Array.isArray(list) ? list : [];
        if (response.status && arr.length >= 0) {
          setConferences(arr);
        } else {
          toast.error(response.message || "Failed to load events");
          setConferences([]);
        }
      } catch (error) {
        toast.error(error?.message || "An error occurred while fetching data");
        setConferences([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredConferences = useMemo(() => {
    let filtered = [...conferences];
    if (selectedFilter !== "all") {
      filtered = filtered.filter((item) => {
        const eventTypes = item.event_type || [];
        const hasConference = eventTypes.some(
          (t) => t?.toLowerCase() === "conference"
        );
        const hasTradeMission = eventTypes.some(
          (t) =>
            t?.toLowerCase() === "trade_mission" ||
            t?.toLowerCase() === "trade_missions"
        );
        if (selectedFilter === "conferences") return hasConference;
        if (selectedFilter === "trade_missions") return hasTradeMission;
        return true;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const title = (item.event_title || "").toLowerCase();
        const desc = (item.about_event || "").toLowerCase();
        const venue = (item.venue_name || "").toLowerCase();
        const city = (item.city || "").toLowerCase();
        const country = (item.country || "").toLowerCase();
        return (
          title.includes(q) ||
          desc.includes(q) ||
          venue.includes(q) ||
          city.includes(q) ||
          country.includes(q)
        );
      });
    }
    return filtered;
  }, [conferences, selectedFilter, searchQuery]);

  const handleCardClick = (item) => {
    if (item.slug) {
      navigate(`/event-media-sales/event-details/${item.slug}`);
    }
  };

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <HeroSlider
        images={heroSlides}
        showBackButton
        backLink="/event-media-sales"
        backLabel="Back to Event & Media Sales"
      />

      <section className="px-4 py-12 bg-gradient-to-b from-gray-50 to-white sm:px-6 lg:px-8 flex-1">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-8">
            <div className="min-w-0">
              <div className="flex flex-wrap gap-2 items-center mb-1">
                <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                  {config.title}
                </h2>
                <span className="inline-flex gap-1 items-center px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full border border-emerald-200">
                  <Sparkles size={12} />
                  For agents
                </span>
              </div>
              <p className="text-sm text-gray-600 max-w-xl">{config.subtitle}</p>
              <div className="mt-2 w-24 h-1 bg-emerald-500 rounded" />
            </div>
            <motion.button
              onClick={() => navigate("/event-media-sales")}
              whileHover={{ scale: 1.03, y: -1 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg shadow-sm transition-colors duration-200 hover:bg-emerald-100 hover:border-emerald-300 shrink-0"
            >
              <ArrowLeft size={16} />
              Back to Event & Media Sales
            </motion.button>
          </div>

          <div className="mb-8">
            <ConferenceFilters
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              placeholder={config.placeholder}
            />
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="flex flex-col gap-4 items-center">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full border-2 border-emerald-200 border-t-emerald-500 animate-spin" />
                </div>
                <p className="text-sm text-gray-600">
                  Loading {config.title.toLowerCase()}...
                </p>
              </div>
            </div>
          ) : filteredConferences.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-center items-center py-16"
            >
              <div className="flex flex-col gap-4 items-center text-center max-w-sm">
                <div className="flex justify-center items-center w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl">
                  <svg
                    className="w-10 h-10 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    No results found
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {searchQuery
                      ? "Try adjusting your search or filter."
                      : `No ${config.title.toLowerCase()} available at the moment.`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/event-media-sales")}
                  className="mt-2 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200 hover:bg-emerald-100 transition-colors"
                >
                  Back to Event & Media Sales
                </button>
              </div>
            </motion.div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={`${selectedFilter}-${searchQuery}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
                className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6 sm:grid-cols-2 lg:grid-cols-4"
              >
                {filteredConferences.map((item, index) => (
                  <motion.div
                    key={item.id ?? item._id ?? index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.05, 0.3) }}
                    className="w-full"
                  >
                    <ConferenceCard
                      item={item}
                      onClick={() => handleCardClick(item)}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          )}

          {!loading && filteredConferences.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-6 text-sm text-gray-600"
            >
              Showing {filteredConferences.length} of {conferences.length} results
            </motion.div>
          )}
        </div>
      </section>

      <AuthenticatedFooter />
    </div>
  );
}
